import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppSelector } from '../../../../store/hooks'
import { selectRoomSessionStartedAt } from '../../../../store/meetingSummary/roomSession'
import { CopyData } from './copy'
import {
  CONSENT_TRACKING_PRESET_ID,
  ConsentResult,
  getUserMetadataConsent,
  mapToUserConsent,
  UserConsent,
  UserConsentState,
} from '../../../../components/Consent/model'
import { AIFeatureInfo, useGetManualParticipantsQuery, useGetRoomSessionQuery, useListRoomEventsQuery } from '../../../../store/media/media'
import { ASSIGNMENT_ROLE_NAME_MAPPING } from './ConfigureAssignment/AssignmentRoleNameMapping'
import { parseMetadata } from './utils'
import { DEFAULT_ROLE } from './hooks/users'
import { User } from '../../../../models/User'
import { StructuredSummaryResult } from '../../../../models/AiResult'
import { useGetAiResultsQuery } from '../../../../store/ai/ai'
import { selectGroupedTasks } from '../../../../store/task/task'
import { useAccount } from '../../../../common/useAccount'
import { type ManualParticipant } from '../../../../models/ManualParticipant'
import { didManualParticipantAttend } from './ConfigureAssignment/utils'

export const useCopyData = (): CopyData => {
  const roomSessionStartedAt = useAppSelector(selectRoomSessionStartedAt)
  const groupedTasks = useAppSelector(selectGroupedTasks)

  return {
    groupedTasks,
    roomSessionStartedAt,
  }
}
const MAX_AI_FEATURE_EVENT_TRIES = 10
const MAX_CONSENT_CHECK_TRIES = 20
const POLLING_INTERVAL = 3000

export interface UseConsentCheckProperties {
  roomId?: string
  sessionId?: string
  skip?: boolean
}

export const useConsentCheck = ({ roomId: roomIdArg, sessionId: sessionIdArg, skip: skipArg }: UseConsentCheckProperties = {}) => {
  const skip = skipArg ?? false
  const { roomId = roomIdArg || '', sessionId = sessionIdArg || '' } = useParams()
  const [consentResultAvailable, setConsentResultAvailable] = useState(false)
  const [consentCheckCounter, setConsentCheckCounter] = useState(0)
  const [aiFeatureEventCheckCounter, setAiFeatureEventCheckCounter] = useState(0)
  const [hasAiFeatureEvents, setHasAiFeatureEvents] = useState(false)

  const { account, accountId, isLoading: accountIsFetching } = useAccount({ refetchOnMountOrArgChange: true })

  const stopPollingAiFeatureEvents = hasAiFeatureEvents || aiFeatureEventCheckCounter >= MAX_AI_FEATURE_EVENT_TRIES

  const { data: aiFeatureExecutedEvents, isFetching: aiFeatureEventsFetching } = useListRoomEventsQuery(
    {
      accountId,
      roomId,
      sessionId,
      pageRequest: { page: 0, size: 500 },
      types: ['aiFeatureExecutionStarted'],
    },
    {
      refetchOnMountOrArgChange: true,
      skip,
      pollingInterval: stopPollingAiFeatureEvents ? 0 : POLLING_INTERVAL,
    },
  )

  useEffect(() => {
    if (!aiFeatureEventsFetching && aiFeatureExecutedEvents?.data) {
      if (aiFeatureExecutedEvents.data.length > 0) {
        setHasAiFeatureEvents(true)
      } else {
        setAiFeatureEventCheckCounter((prev) => prev + 1)
      }
    }
  }, [aiFeatureEventsFetching, aiFeatureExecutedEvents])

  const consentTrackingFeatureExecuted = useMemo(() => {
    if (aiFeatureExecutedEvents?.data && hasAiFeatureEvents) {
      return aiFeatureExecutedEvents.data
        .filter((event) => event.type === 'aiFeatureExecutionStarted')
        .flatMap((event) => event.metadata?.executedAiFeatures ?? [])
        .some((feature: AIFeatureInfo) => feature.presetId === CONSENT_TRACKING_PRESET_ID)
    }
    return false
  }, [aiFeatureExecutedEvents, hasAiFeatureEvents])

  const { data: roomSession, isLoading: roomIsFetching } = useGetRoomSessionQuery(
    {
      accountId,
      roomId,
      sessionId,
    },
    { refetchOnMountOrArgChange: false, skip },
  )

  const { data: manualParticipantsResults, isLoading: manualParticipantsAreLoading } = useGetManualParticipantsQuery(
    { accountId, roomId, sessionId },
    { refetchOnMountOrArgChange: false, skip },
  )

  const attendingClientUsers = useMemo(() => {
    if (!roomSession || !roomSession.data || !manualParticipantsResults) {
      return null
    }
    const manualParticipantsByUserId = (manualParticipantsResults.data ?? []).reduce<Record<string, ManualParticipant>>((acc, participant) => {
      acc[participant.userId] = participant
      return acc
    }, {})
    return roomSession.data.users.filter((user) => {
      if (user.id.startsWith('speaker_')) {
        return false
      }
      const roleName = !user.roles.length ? DEFAULT_ROLE : (ASSIGNMENT_ROLE_NAME_MAPPING[user.roles[0].id] ?? user.roles[0].name)
      return roleName === 'Clients' && didManualParticipantAttend(manualParticipantsByUserId[user.id]?.addedReason)
    })
  }, [roomSession, manualParticipantsResults])

  const noAttendingClients = attendingClientUsers !== null && attendingClientUsers.length === 0

  const shouldPollConsent = hasAiFeatureEvents && consentTrackingFeatureExecuted && !consentResultAvailable && !noAttendingClients
  const consentCheckInterval = shouldPollConsent && consentCheckCounter < MAX_CONSENT_CHECK_TRIES ? POLLING_INTERVAL : 0

  const { data: consentResults, isFetching: consentResultsIsFetching } = useGetAiResultsQuery(
    {
      accountId,
      sessionId: sessionId,
      roomId: roomId,
      presetId: CONSENT_TRACKING_PRESET_ID,
      pageRequest: { page: 0, size: 500 },
    },
    {
      refetchOnMountOrArgChange: true,
      skip: skip || !hasAiFeatureEvents || !consentTrackingFeatureExecuted || consentCheckCounter >= MAX_CONSENT_CHECK_TRIES || !roomSession,
      pollingInterval: consentCheckInterval,
    },
  )

  useEffect(() => {
    if (!consentResultsIsFetching && consentResults?.data) {
      if (consentResults.data.length > 0) {
        setConsentResultAvailable(true) // eslint-disable-line react-hooks/set-state-in-effect
      } else {
        setConsentCheckCounter((prev) => prev + 1)
      }
    }
  }, [consentResultsIsFetching, consentResults])
  const noConsentFeatureExecuted = hasAiFeatureEvents && !consentTrackingFeatureExecuted

  const usersConsent = useMemo(() => {
    if (skip || !roomSession || !roomSession.data) {
      return null
    }

    if (!noConsentFeatureExecuted && (!consentResults || !consentResults.data)) {
      return null
    }

    const { users, id: sessionId, roomId } = roomSession.data

    return users
      .filter((user) => !user.id.startsWith('speaker_'))
      .reduce((acc: { [key: string]: UserConsent | null }, user: User) => {
        const userConsent = getUserMetadataConsent(user)
        if (userConsent) {
          acc[user.id] = userConsent
        } else if (!noConsentFeatureExecuted && consentResults?.data) {
          const consent = consentResults.data.find((r) => r.userId === user.id)
          acc[user.id] = consent ? mapToUserConsent((consent.result as StructuredSummaryResult).result as ConsentResult, user, roomId, sessionId) : null
        } else {
          acc[user.id] = null
        }
        return acc
      }, {})
  }, [roomSession, consentResults, skip, noConsentFeatureExecuted])

  const meetingDisabledByConsent = useMemo(() => {
    if (!account || !attendingClientUsers) {
      return false
    }
    if (!account.effectiveConfig.complianceConfig.consentApprovalCheck) {
      return false
    }
    const everyConsentAccepted = attendingClientUsers
      .map((client) => {
        return usersConsent ? usersConsent[client.id] : null
      })
      .map((userConsent) => userConsent?.current?.state ?? UserConsentState.UNKNOWN)
      .every((consentState) => consentState === UserConsentState.ACCEPTED)
    return !everyConsentAccepted
  }, [account, attendingClientUsers, usersConsent])

  const meetingUnlocked = useMemo(() => {
    if (!roomSession || !roomSession?.data) {
      return false
    }
    const roomSessionMetadata = parseMetadata(roomSession.data.metadata)
    return roomSessionMetadata?.external?.meetingUnlocked === true
  }, [roomSession])

  const meetingApprovalCheck = useMemo(() => {
    return account && account.effectiveConfig.complianceConfig.userConsentTrackingEnabled && account.effectiveConfig.complianceConfig.consentApprovalCheck
  }, [account])

  const consentStillLoading = !hasAiFeatureEvents
    ? aiFeatureEventCheckCounter < MAX_AI_FEATURE_EVENT_TRIES
    : consentTrackingFeatureExecuted && !consentResultAvailable && !noAttendingClients && consentCheckCounter < MAX_CONSENT_CHECK_TRIES

  return {
    meetingDisabledByConsent,
    usersConsent,
    consentLoading: roomIsFetching || accountIsFetching || manualParticipantsAreLoading || consentStillLoading,
    meetingUnlocked,
    meetingApprovalCheck,
  }
}
